// src/pages/client/ProviderDetailPage.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  Star,
  MapPin,
  Phone,
  Mail,
  MessageCircle,
  Share2,
  Calendar,
  Clock,
  CheckCircle,
  Award,
  Briefcase,
  DollarSign,
  Navigation,
  X,
  Send,
  User,
  Wrench,
  Languages,
  Heart,
  Loader2,
  Star as StarIcon,
  ChevronLeft,
  ExternalLink,
  ThumbsUp,
  MessageSquare,
  Users,
  Image,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize,
  Minimize
} from 'lucide-react';

import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import useIPLocation from '../../hooks/useIPLocation';
import colors from '../../constants/colors';
import {
  getProviderById,
  selectCurrentProvider,
  selectProviderLoading,
  selectProviderError,
  clearCurrentProvider
} from '../../store/slices/providerSlice';
import {
  getReviews,
  addReview,
  getRatingSummary,
  selectReviews,
  selectReviewStatistics,
  selectRatingSummary,
  selectReviewsLoading,
  selectSubmittingReview,
  selectSubmitReviewSuccess,
  selectSubmitReviewError,
  resetReviewState,
  clearReviewSuccess,
  clearReviewErrors
} from '../../store/slices/serviceProviderReviewSlice';
// Import gallery selectors and actions
import {
  getGalleryImages,
  selectGalleryImages,
  selectGalleryLoading,
  selectGalleryError
} from '../../store/slices/providerGalleryServiceSlice';
import styles from '../../styles/ProviderDetailPage.module.css';

import formatAddress from '../../utils/addressHelper';
// ======================================================
// CONSTANTS
// ======================================================
const HAITIAN_CITIES = [
  'port-au-prince', 'cap-haïtien', 'cap-haitien', 'jacmel', 'gonaïves', 'gonaives',
  'les cayes', 'jérémie', 'jeremie', 'hinche', 'port-de-paix', 'petit-goâve',
  'petit-goave', 'miragôane', 'miragowane', 'saint-marc', 'saint marc',
  'croix-des-bouquets', 'croix des bouquets', 'delmas', 'pétion-ville', 'pétionville',
  'carrefour', 'kenscoff', 'leogâne', 'leogane', 'grand-goâve', 'grand-goave'
];

// ======================================================
// GALLERY COMPONENT WITH ZOOM AND SLIDER
// ======================================================
const ProviderGallery = ({ images, onImageClick }) => {
  const { translatedText: viewAll } = useTranslation('View All');
  const { translatedText: galleryLabel } = useTranslation('Gallery');
  
  if (!images || images.length === 0) {
    return null;
  }

  // Show only first 4 images in the preview
  const displayImages = images.slice(0, 4);
  const remainingCount = images.length - 4;

  // Normalize Cloudinary URLs
  const normalizeImageUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    let normalized = url.trim();
    const markdownMatch = normalized.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (markdownMatch) {
      normalized = markdownMatch[2].trim();
    }
    normalized = normalized.replace(
      '/image/upload/t_w_300,h_200,c_thumb,q_auto/',
      '/image/upload/w_300,h_200,c_thumb,q_auto/'
    );
    return normalized;
  };

  return (
    <div className={styles.gallerySection}>
      <div className={styles.galleryHeader}>
        <h3>
          <Image size={20} />
          {galleryLabel}
        </h3>
        {images.length > 4 && (
          <button 
            className={styles.viewAllBtn}
            onClick={() => onImageClick && onImageClick()}
          >
            {viewAll} ({images.length})
          </button>
        )}
      </div>
      <div className={styles.galleryGrid}>
        {displayImages.map((image, index) => {
          const imageUrl = normalizeImageUrl(image.url || image.thumbnail);
          const caption = image.caption || image.title || '';
          return (
            <div 
              key={image._id || index} 
              className={styles.galleryItem}
              onClick={() => onImageClick && onImageClick(image)}
            >
              <img 
                src={imageUrl} 
                alt={caption || `Gallery image ${index + 1}`}
                className={styles.galleryThumbnail}
                loading="lazy"
                onError={(e) => {
                  const fallback = normalizeImageUrl(image.thumbnail);
                  if (fallback && e.target.src !== fallback) {
                    e.target.src = fallback;
                  }
                }}
              />
              {index === 3 && remainingCount > 0 && (
                <div className={styles.galleryMoreOverlay}>
                  <span>+{remainingCount}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ======================================================
// GALLERY MODAL WITH ZOOM AND SLIDER
// ======================================================
const GalleryModal = ({ images, onClose, initialImage }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  
  const { translatedText: close } = useTranslation('Close');
  const { translatedText: galleryLabel } = useTranslation('Gallery');

  useEffect(() => {
    if (initialImage) {
      const index = images.findIndex(img => img._id === initialImage._id);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [initialImage, images]);

  // Reset zoom when changing images
  useEffect(() => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [currentIndex]);

  const normalizeImageUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    let normalized = url.trim();
    const markdownMatch = normalized.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (markdownMatch) {
      normalized = markdownMatch[2].trim();
    }
    normalized = normalized.replace(
      '/image/upload/t_w_300,h_200,c_thumb,q_auto/',
      '/image/upload/w_300,h_200,c_thumb,q_auto/'
    );
    return normalized;
  };

  const goToPrevious = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const goToNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowUp') {
        handleZoomIn();
      } else if (e.key === 'ArrowDown') {
        handleZoomOut();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, currentIndex, images.length]);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
    // Reset position if zooming in from 1
    if (zoomLevel === 1) {
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    if (zoomLevel <= 0.75) {
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.log('Fullscreen not supported');
      });
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Mouse drag for panning
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setStartPosition({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      const newX = e.clientX - startPosition.x;
      const newY = e.clientY - startPosition.y;
      
      // Calculate bounds
      const maxX = (zoomLevel - 1) * 200;
      const maxY = (zoomLevel - 1) * 200;
      
      setPosition({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY))
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch events for mobile
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    });
    if (zoomLevel > 1) {
      setIsDragging(true);
      setStartPosition({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y
      });
    }
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;
    
    if (zoomLevel > 1) {
      e.preventDefault();
      const newX = touch.clientX - startPosition.x;
      const newY = touch.clientY - startPosition.y;
      const maxX = (zoomLevel - 1) * 200;
      const maxY = (zoomLevel - 1) * 200;
      setPosition({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY))
      });
    } else {
      // Swipe detection for image navigation
      if (Math.abs(deltaX) > 50 && Math.abs(deltaY) < 50) {
        if (deltaX > 0) {
          goToPrevious();
        } else {
          goToNext();
        }
        setTouchStart(null);
      }
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setTouchStart(null);
  };

  // Mouse wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  if (!images || images.length === 0) return null;

  const currentImage = images[currentIndex];
  const imageUrl = normalizeImageUrl(currentImage?.url || currentImage?.thumbnail);
  const caption = currentImage?.caption || currentImage?.title || '';

  return (
    <div className={styles.galleryModalOverlay} onClick={onClose}>
      <div className={styles.galleryModalContent} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles.galleryModalHeader}>
          <button className={styles.galleryModalClose} onClick={onClose}>
            <X size={24} />
          </button>
          <span className={styles.galleryModalCounter}>
            {currentIndex + 1} / {images.length}
          </span>
          <div className={styles.galleryModalControls}>
            <button 
              className={styles.galleryModalControlBtn}
              onClick={handleZoomIn}
              title="Zoom In"
            >
              <ZoomIn size={20} />
            </button>
            <button 
              className={styles.galleryModalControlBtn}
              onClick={handleZoomOut}
              title="Zoom Out"
            >
              <ZoomOut size={20} />
            </button>
            <button 
              className={styles.galleryModalControlBtn}
              onClick={handleResetZoom}
              title="Reset Zoom"
            >
              <span className={styles.zoomLevel}>{Math.round(zoomLevel * 100)}%</span>
            </button>
            <button 
              className={styles.galleryModalControlBtn}
              onClick={toggleFullscreen}
              title="Fullscreen"
            >
              {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
            </button>
          </div>
        </div>

        {/* Image Container */}
        <div 
          className={styles.galleryModalImageContainer}
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
        >
          <div 
            className={styles.galleryModalImageWrapper}
            style={{
              transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
              cursor: zoomLevel > 1 ? 'grab' : 'default',
              transition: isDragging ? 'none' : 'transform 0.2s ease'
            }}
          >
            <img 
              ref={imageRef}
              src={imageUrl} 
              alt={caption || `Gallery image ${currentIndex + 1}`}
              className={styles.galleryModalImage}
              draggable={false}
              onError={(e) => {
                const fallback = normalizeImageUrl(currentImage?.thumbnail);
                if (fallback && e.target.src !== fallback) {
                  e.target.src = fallback;
                }
              }}
            />
          </div>

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button 
                className={`${styles.galleryModalNav} ${styles.galleryModalNavPrev}`}
                onClick={goToPrevious}
                disabled={isTransitioning}
              >
                <ChevronLeft size={32} />
              </button>
              <button 
                className={`${styles.galleryModalNav} ${styles.galleryModalNavNext}`}
                onClick={goToNext}
                disabled={isTransitioning}
              >
                <ChevronRight size={32} />
              </button>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className={styles.galleryModalFooter}>
          {caption && <p className={styles.galleryModalCaption}>{caption}</p>}
          <div className={styles.galleryModalThumbnails}>
            {images.map((img, idx) => {
              const thumbUrl = normalizeImageUrl(img.thumbnail || img.url);
              return (
                <div
                  key={img._id || idx}
                  className={`${styles.galleryModalThumbnail} ${idx === currentIndex ? styles.active : ''}`}
                  onClick={() => {
                    setCurrentIndex(idx);
                    setZoomLevel(1);
                    setPosition({ x: 0, y: 0 });
                  }}
                >
                  <img 
                    src={thumbUrl} 
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      const fallback = normalizeImageUrl(img.url);
                      if (fallback && e.target.src !== fallback) {
                        e.target.src = fallback;
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ======================================================
// SEPARATE COMPONENT FOR REVIEW ITEM
// ======================================================
const ReviewItem = ({ review }) => {
  const { translatedText: translatedComment } = useTranslation(review.comment || '');
  
  return (
    <div className={styles.reviewCard}>
      <div className={styles.reviewHeader}>
        <div className={styles.reviewerInfo}>
          <div className={styles.reviewerAvatar}>
            <span>{review.customerName?.charAt(0) || 'U'}</span>
          </div>
          <div>
            <div className={styles.reviewerName}>{review.customerName || 'Anonymous'}</div>
            <div className={styles.reviewStars}>
              {[...Array(5)].map((_, i) => (
                <StarIcon 
                  key={i} 
                  size={14} 
                  color={i < (review.rating || 0) ? colors.accentGold : colors.gray300}
                  fill={i < (review.rating || 0) ? colors.accentGold : 'none'}
                />
              ))}
            </div>
          </div>
        </div>
        <span className={styles.reviewDate}>
          {review.date ? new Date(review.date).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          }) : ''}
        </span>
      </div>
      <p className={styles.reviewComment}>{translatedComment}</p>
    </div>
  );
};

// ======================================================
// SEPARATE COMPONENT FOR SPECIALTY ITEM
// ======================================================
const SpecialtyItem = ({ specialty }) => {
  const { translatedText } = useTranslation(specialty);
  return (
    <span className={styles.specialtyTag}>
      <CheckCircle size={14} className={styles.specialtyIcon} />
      {translatedText}
    </span>
  );
};

// ======================================================
// SEPARATE COMPONENT FOR LANGUAGE TAG
// ======================================================
const LanguageTag = ({ language }) => {
  const { translatedText } = useTranslation(language);
  return <span className={styles.languageTag}>{translatedText}</span>;
};

// ======================================================
// MAIN PROVIDER DETAIL PAGE COMPONENT
// ======================================================
const ProviderDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { providerId } = useParams(); // ← FIXED: Use providerId from params
  const dispatch = useDispatch();
  
  // Redux state
  const provider = useSelector(selectCurrentProvider);
  const isLoading = useSelector(selectProviderLoading);
  const providerError = useSelector(selectProviderError);
  const reviews = useSelector(selectReviews);
  const reviewStatistics = useSelector(selectReviewStatistics);
  const ratingSummary = useSelector(selectRatingSummary);
  const reviewsLoading = useSelector(selectReviewsLoading);
  const isSubmitting = useSelector(selectSubmittingReview);
  const submitSuccess = useSelector(selectSubmitReviewSuccess);
  const submitError = useSelector(selectSubmitReviewError);

  // Gallery state
  const galleryImages = useSelector(selectGalleryImages);
  const galleryLoading = useSelector(selectGalleryLoading);
  const galleryError = useSelector(selectGalleryError);

  // ======================================================
  // IP LOCATION HOOK
  // ======================================================
  const {
    position: ipLocation,
    error: locationError,
    loading: locationLoading,
    hasLocation: hasIPLocation,
    locationString,
    refreshLocation
  } = useIPLocation({
    autoLoad: true,
    maxRetries: 2,
    retryDelay: 1000,
    cacheEnabled: true
  });

  // Local state
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [distanceDisplay, setDistanceDisplay] = useState('N/A');
  
  // Review Modal States
  const [modalVisible, setModalVisible] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [selectedStar, setSelectedStar] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Gallery Modal States
  const [galleryModalVisible, setGalleryModalVisible] = useState(false);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);

  const { currentLanguage } = useLanguage();

  // ======================================================
  // ✅ ALL TRANSLATIONS
  // ======================================================
  const { translatedText: verified } = useTranslation('Verified');
  const { translatedText: yearsExperience } = useTranslation('years experience');
  const { translatedText: reviewsLabel } = useTranslation('reviews');
  const { translatedText: distance } = useTranslation('Distance');
  const { translatedText: about } = useTranslation('About');
  const { translatedText: readMore } = useTranslation('Read More');
  const { translatedText: readLess } = useTranslation('Read Less');
  const { translatedText: specialties } = useTranslation('Specialties');
  const { translatedText: languagesLabel } = useTranslation('Languages');
  const { translatedText: workingHours } = useTranslation('Working Hours');
  const { translatedText: closed } = useTranslation('Closed');
  const { translatedText: writeReview } = useTranslation('Write a Review');
  const { translatedText: noReviewsYet } = useTranslation('No reviews yet');
  const { translatedText: beFirstReview } = useTranslation('Be the first to review this provider!');
  const { translatedText: contact } = useTranslation('Contact');
  const { translatedText: directions } = useTranslation('Directions');
  const { translatedText: haitiLabel } = useTranslation('Haiti');
  const { translatedText: diasporaLabel } = useTranslation('Diaspora');
  const { translatedText: monday } = useTranslation('Monday');
  const { translatedText: tuesday } = useTranslation('Tuesday');
  const { translatedText: wednesday } = useTranslation('Wednesday');
  const { translatedText: thursday } = useTranslation('Thursday');
  const { translatedText: friday } = useTranslation('Friday');
  const { translatedText: saturday } = useTranslation('Saturday');
  const { translatedText: sunday } = useTranslation('Sunday');
  const { translatedText: writeReviewTitle } = useTranslation('Write a Review');
  const { translatedText: yourRating } = useTranslation('Your Rating');
  const { translatedText: tapStarToRate } = useTranslation('Tap a star to rate');
  const { translatedText: yourName } = useTranslation('Your Name *');
  const { translatedText: namePlaceholder } = useTranslation('Enter your full name');
  const { translatedText: phoneOptional } = useTranslation('Phone Number (Optional)');
  const { translatedText: phonePlaceholder } = useTranslation('Enter your phone number');
  const { translatedText: yourReview } = useTranslation('Your Review *');
  const { translatedText: reviewPlaceholder } = useTranslation('Share your experience with this provider...');
  const { translatedText: submitReview } = useTranslation('Submit Review');
  const { translatedText: starLabel } = useTranslation('star');
  const { translatedText: starsLabel } = useTranslation('stars');
  const { translatedText: errorTitle } = useTranslation('Error');
  const { translatedText: enterName } = useTranslation('Please enter your name');
  const { translatedText: selectRating } = useTranslation('Please select a rating');
  const { translatedText: writeComment } = useTranslation('Please write a comment');
  const { translatedText: reviewSubmitted } = useTranslation('Review Submitted!');
  const { translatedText: thankYouFeedback } = useTranslation('Thank you for your feedback!');
  const { translatedText: submitFailed } = useTranslation('Failed to submit review. Please try again.');
  const { translatedText: homeTitle } = useTranslation('Haitian Services Platform');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide, One Service at a Time');
  const { translatedText: madeWithLove } = useTranslation('All rights reserved');
  const { translatedText: loadingProvider } = useTranslation('Loading provider details...');
  const { translatedText: providerNotFound } = useTranslation('Provider not found');
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboard } = useTranslation('Dashboard');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');

  // Translate provider data
  const translatedServiceType = useTranslation(provider?.serviceType || '').translatedText;
  const translatedDescription = useTranslation(provider?.description || '').translatedText;
  const translatedBusinessName = useTranslation(provider?.businessName || provider?.name || '').translatedText;

  const dayNames = {
    monday: monday,
    tuesday: tuesday,
    wednesday: wednesday,
    thursday: thursday,
    friday: friday,
    saturday: saturday,
    sunday: sunday
  };

  // ======================================================
  // HELPER: Get provider coordinates
  // ======================================================
  const getProviderCoordinates = useCallback((providerData) => {
    try {
      if (providerData?.location?.coordinates) {
        const coords = providerData.location.coordinates;
        if (Array.isArray(coords) && coords.length === 2) {
          return { lat: coords[1], lng: coords[0] };
        }
      }
      if (providerData?.coordinates) {
        return { lat: providerData.coordinates.lat, lng: providerData.coordinates.lng };
      }
      return null;
    } catch (error) {
      console.warn('Error getting provider coordinates:', error);
      return null;
    }
  }, []);

  // ======================================================
  // CALCULATE DISTANCE
  // ======================================================
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 3959; // Miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);

  // ======================================================
  // CHECK IF PROVIDER IS IN HAITI
  // ======================================================
  const isProviderInHaiti = useCallback((providerData) => {
    if (!providerData || !providerData.address) return false;
    
    const country = providerData.address.country?.toLowerCase() || '';
    const city = providerData.address.city?.toLowerCase() || '';
    
    if (country === 'haiti' || country === 'ht' || country === 'haïti') {
      return true;
    }
    
    if (HAITIAN_CITIES.some(haitianCity => city.includes(haitianCity))) {
      return true;
    }
    
    return false;
  }, []);

  // ======================================================
  // ✅ HELPER: Get display image (cover first, then profile)
  // ======================================================
  const getDisplayImage = useCallback(() => {
    if (provider?.coverImage?.url) {
      return provider.coverImage.url;
    }
    if (provider?.profileImage?.url) {
      return provider.profileImage.url;
    }
    return 'https://via.placeholder.com/400';
  }, [provider]);

  // ======================================================
  // CALCULATE PROVIDER DISTANCE
  // ======================================================
  const calculateProviderDistance = useCallback(() => {
    if (!provider) return 'N/A';
    
    const coords = getProviderCoordinates(provider);
    if (coords && ipLocation && ipLocation.latitude && ipLocation.longitude) {
      try {
        const distance = calculateDistance(
          ipLocation.latitude,
          ipLocation.longitude,
          coords.lat,
          coords.lng
        );
        if (distance < 0.1) {
          return '< 0.1 mi';
        } else if (distance < 1) {
          return `${(distance * 5280).toFixed(0)} ft`;
        } else {
          return `${distance.toFixed(1)} mi`;
        }
      } catch (err) {
        console.warn('Distance calculation error:', err);
        return 'N/A';
      }
    }
    return 'N/A';
  }, [provider, ipLocation, calculateDistance, getProviderCoordinates]);

  // ======================================================
  // LOAD PROVIDER DATA - FIXED
  // ======================================================
  const loadProviderData = useCallback(async () => {
    // Use providerId from params, or fallback to state
    const id = providerId || location.state?.provider?._id || location.state?.provider?.id;
    
    if (!id) {
      console.error('No provider ID found');
      return;
    }
    
    console.log('📌 Loading provider with ID:', id);
    
    try {
      // FIX: getProviderById expects an object with id
      const result = await dispatch(getProviderById({ id })).unwrap();
      console.log('✅ Provider loaded:', result);
      
      // Load reviews
      if (id) {
        await dispatch(getReviews({ providerId: id })).unwrap();
        await dispatch(getRatingSummary(id)).unwrap();
        
        // Load gallery images
        await dispatch(getGalleryImages({ providerId: id, limit: 20 })).unwrap();
      }
      
      return result;
    } catch (error) {
      console.error('Failed to load provider data:', error);
    }
  }, [dispatch, providerId, location.state]);

  // ======================================================
  // INITIAL LOAD - FIXED
  // ======================================================
  useEffect(() => {
    // Check if we have a providerId from params or from location state
    const hasId = providerId || location.state?.provider?._id || location.state?.provider?.id;
    
    if (hasId) {
      loadProviderData();
    } else {
      console.warn('No provider ID found in URL or state');
    }
    
    return () => {
      dispatch(clearCurrentProvider());
      dispatch(resetReviewState());
    };
  }, [providerId, location.state?.provider?._id, location.state?.provider?.id]);

  // ======================================================
  // UPDATE DISTANCE WHEN LOCATION CHANGES
  // ======================================================
  useEffect(() => {
    if (provider && ipLocation) {
      setDistanceDisplay(calculateProviderDistance());
    }
  }, [provider, ipLocation, calculateProviderDistance]);

  // ======================================================
  // HANDLE SUBMIT SUCCESS
  // ======================================================
  useEffect(() => {
    if (submitSuccess) {
      alert(`${reviewSubmitted}\n\n${thankYouFeedback}`);
      dispatch(clearReviewSuccess());
      resetReviewForm();
      setModalVisible(false);
      // Reload reviews
      if (provider?._id || provider?.id) {
        const providerIdValue = provider._id || provider.id;
        dispatch(getReviews({ providerId: providerIdValue }));
        dispatch(getRatingSummary(providerIdValue));
      }
    }
  }, [submitSuccess, dispatch, provider]);

  // ======================================================
  // HANDLE SUBMIT ERROR
  // ======================================================
  useEffect(() => {
    if (submitError) {
      alert(`${errorTitle}\n\n${submitError}`);
      dispatch(clearReviewErrors());
    }
  }, [submitError, dispatch]);

  // ======================================================
  // HANDLE PROVIDER ERROR - FIXED
  // ======================================================
  useEffect(() => {
    if (providerError) {
      console.error('Provider error:', providerError);
      // Don't show alert for "not found" errors, just show the error state
      if (providerError !== 'Provider not found') {
        alert(`${errorTitle}\n\n${providerError}`);
      }
    }
  }, [providerError, dispatch, errorTitle]);

  // ======================================================
  // HANDLERS
  // ======================================================

  
// HANDLE CALL
// Desktop/Computer: show the phone number
// Mobile: attempt to open the phone dialer
// ======================================================
const handleCall = () => {
  if (!provider?.phone) {
    alert('Phone number is not available.');
    return;
  }

  const phone = String(provider.phone).trim();

  // Detect mobile/tablet devices
  const isMobileDevice =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  // -----------------------------------------------
  // MOBILE
  // -----------------------------------------------
  if (isMobileDevice) {
    window.location.href = `tel:${phone}`;
    return;
  }

  // -----------------------------------------------
  // DESKTOP / COMPUTER
  // Show the phone number instead of trying to
  // launch a phone application.
  // -----------------------------------------------
  alert(`Phone Number:\n\n${phone}`);
};

  const handleWhatsApp = () => {
    if (provider?.phone) {
      const phone = provider.phone.replace(/\s/g, '');
      window.open(`https://wa.me/${phone}`, '_blank');
    }
  };

  const handleEmail = () => {
    if (provider?.email) {
      window.location.href = `mailto:${provider.email}`;
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: provider.businessName || provider.name,
          text: `Check out ${provider.businessName || provider.name} on Haitian Services!`,
          url: window.location.href,
        });
      } catch (error) {
        console.error('Share error:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert('Link copied to clipboard!');
      });
    }
  };

// ======================================================
// OPEN PROVIDER LOCATION IN GOOGLE MAPS
// ======================================================
const handleDirection = () => {
  if (!provider) {
    alert('Provider information is not available.');
    return;
  }

  const coords = getProviderCoordinates(provider);

  // ----------------------------------------------------
  // Build a complete readable address
  // ----------------------------------------------------
  const address = provider.address || {};

  const addressParts = [
    address.street,
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.province,
    address.zipCode,
    address.postalCode,
    address.country
  ].filter(Boolean);

  let fullAddress = addressParts.join(', ').trim();

  // If formatAddress() produces a better address, use it
  if (!fullAddress && provider.address) {
    try {
      fullAddress = formatAddress(provider.address);
    } catch (error) {
      console.warn('Could not format provider address:', error);
    }
  }

  // ----------------------------------------------------
  // Remove accidental duplicate commas/spaces
  // ----------------------------------------------------
  if (fullAddress) {
    fullAddress = fullAddress
      .replace(/\s+/g, ' ')
      .replace(/,\s*,+/g, ',')
      .trim();
  }

  console.log('📍 Provider address:', fullAddress);
  console.log('📍 Provider coordinates:', coords);

  // ----------------------------------------------------
  // PREFERRED:
  // Search Google Maps using the actual address.
  // This makes Google Maps display the provider address.
  // ----------------------------------------------------
  if (fullAddress) {
    const googleMapsUrl =
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;

    console.log('🗺️ Opening Google Maps with address:', googleMapsUrl);

    window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  // ----------------------------------------------------
  // FALLBACK:
  // If there is no address, use coordinates.
  // ----------------------------------------------------
  if (coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng)) {
    const googleMapsUrl =
      `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`;

    console.log('🗺️ Opening Google Maps with coordinates:', googleMapsUrl);

    window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  // ----------------------------------------------------
  // Nothing available
  // ----------------------------------------------------
  alert('Provider address and location are not available.');
};

  const handleStarPress = (starValue) => {
    setRating(starValue);
    setSelectedStar(starValue);
  };

  const handleSubmitReview = async () => {
    if (!customerName.trim()) {
      alert(`${errorTitle}\n\n${enterName}`);
      return;
    }
    if (rating === 0) {
      alert(`${errorTitle}\n\n${selectRating}`);
      return;
    }
    if (!comment.trim()) {
      alert(`${errorTitle}\n\n${writeComment}`);
      return;
    }

    const providerIdValue = provider._id || provider.id;
    const reviewData = {
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim() || undefined,
      rating: rating,
      comment: comment.trim(),
    };

    try {
      await dispatch(addReview({ providerId: providerIdValue, reviewData })).unwrap();
    } catch (error) {
      console.error('Submit review error:', error);
    }
  };

  const resetReviewForm = () => {
    setCustomerName('');
    setCustomerPhone('');
    setRating(0);
    setSelectedStar(0);
    setComment('');
  };

  const renderStars = () => {
    return (
      <div className={styles.starSelector}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            className={styles.starBtn}
            onClick={() => handleStarPress(star)}
          >
            <StarIcon 
              size={40} 
              color={star <= selectedStar ? colors.accentGold : colors.gray300}
              fill={star <= selectedStar ? colors.accentGold : 'none'}
            />
          </button>
        ))}
      </div>
    );
  };

  // ======================================================
  // GALLERY HANDLERS
  // ======================================================
  const handleOpenGallery = (image) => {
    setSelectedGalleryImage(image || null);
    setGalleryModalVisible(true);
  };

  const handleCloseGallery = () => {
    setGalleryModalVisible(false);
    setSelectedGalleryImage(null);
  };

  // ======================================================
  // EARLY RETURNS - FIXED
  // ======================================================
  
  if (isLoading && !provider) {
    return (
      <div className={styles.loadingState}>
        <Loader2 size={40} className={styles.loadingSpinner} />
        <p>{loadingProvider}</p>
      </div>
    );
  }

  if (!provider && !isLoading) {
    return (
      <div className={styles.errorState}>
        <div className={styles.errorContent}>
          <h2>{providerNotFound}</h2>
          <p>Unable to load provider details. The provider may not exist or there was a network error.</p>
          <button className={styles.errorButton} onClick={() => navigate('/providers')}>
            Back to Providers
          </button>
          <button 
            className={styles.errorButton} 
            onClick={() => loadProviderData()}
            style={{ marginTop: '10px', backgroundColor: colors.primary }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Get community
  const isInHaiti = isProviderInHaiti(provider);
  const community = isInHaiti ? haitiLabel : diasporaLabel;
  const communityEmoji = isInHaiti ? '🇭🇹' : '🌍';

  // Get total jobs from reviews - FIXED: Use a different variable name
  const totalJobs = reviewStatistics?.totalReviews || provider?.reviewCount || 0;
  // REMOVED: const providerId = provider?._id || provider?.id; - This was causing the duplicate declaration error

  // ======================================================
  // MAIN RENDER
  // ======================================================
  return (
    <div className={styles.providerDetailPage}>
      {/* Header with Back Button */}
      <div className={styles.detailHeader}>
        <div className={styles.headerContent}>
          <button className={styles.backBtn} onClick={() => navigate('/providers')}>
            <ChevronLeft size={24} />
            <span>Back</span>
          </button>
          <button className={styles.shareBtn} onClick={handleShare}>
            <Share2 size={20} />
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroImageWrapper}>
          <img 
            src={getDisplayImage()} 
            alt={translatedBusinessName}
            className={styles.heroImage}
          />
          <div className={styles.heroBadges}>
            {provider?.verified && (
              <span className={`${styles.badge} ${styles.verifiedBadge}`}>
                <CheckCircle size={16} />
                {verified}
              </span>
            )}
            <span className={`${styles.badge} ${styles.communityBadge}`}>
              {communityEmoji} {community}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.contentGrid}>
          {/* Left Column - Provider Info */}
          <div className={styles.leftColumn}>
            <div className={styles.providerCard}>
              <h1 className={styles.providerName}>{translatedBusinessName}</h1>
              <div className={styles.providerService}>{translatedServiceType}</div>
              
              <div className={styles.providerMeta}>
                <div className={styles.metaItem}>
                  <Clock size={16} />
                  <span>{provider?.yearsOfExperience || 0} {yearsExperience}</span>
                </div>
              </div>

              <div className={styles.ratingSection}>
                <div className={styles.starsDisplay}>
                  {[...Array(5)].map((_, i) => (
                    <StarIcon 
                      key={i} 
                      size={20} 
                      color={i < Math.floor(provider?.rating || 0) ? colors.accentGold : colors.gray300}
                      fill={i < Math.floor(provider?.rating || 0) ? colors.accentGold : 'none'}
                    />
                  ))}
                </div>
                <span className={styles.ratingValue}>{provider?.rating?.toFixed(1) || '0.0'}</span>
                <span className={styles.reviewCount}>({provider?.reviewCount || 0} {reviewsLabel})</span>
              </div>

              <div className={styles.quickStats}>
                <div className={styles.stat}>
                  <MapPin size={20} className={styles.statIcon} />
                  <div>
                    <div className={styles.statLabel}>{distance}</div>
                    <div className={styles.statValue}>{distanceDisplay}</div>
                  </div>
                </div>
              </div>

              {provider?.address && (
                <div className={styles.addressSection}>
                  <MapPin size={18} />
                  <span>
                    {formatAddress(provider.address)}
                   
                  </span>
                </div>
              )}

              {provider?.description && (
                <div className={styles.descriptionSection}>
                  <h3>{about}</h3>
                  <p className={showFullDescription ? styles.expanded : styles.collapsed}>
                    {translatedDescription}
                  </p>
                  {translatedDescription?.length > 200 && (
                    <button className={styles.readMore} onClick={() => setShowFullDescription(!showFullDescription)}>
                      {showFullDescription ? readLess : readMore}
                    </button>
                  )}
                </div>
              )}

              {provider?.specialties && provider.specialties.length > 0 && (
                <div className={styles.specialtiesSection}>
                  <h3>{specialties}</h3>
                  <div className={styles.specialtiesList}>
                    {provider.specialties.map((specialty, index) => (
                      <SpecialtyItem key={index} specialty={specialty} />
                    ))}
                  </div>
                </div>
              )}

              {provider?.languages && provider.languages.length > 0 && (
                <div className={styles.languagesSection}>
                  <h3>{languagesLabel}</h3>
                  <div className={styles.languagesList}>
                    {provider.languages.map((lang, index) => (
                      <LanguageTag key={index} language={lang} />
                    ))}
                  </div>
                </div>
              )}

              {provider?.availability && (
                <div className={styles.availabilitySection}>
                  <h3>{workingHours}</h3>
                  <div className={styles.availabilityGrid}>
                    {Object.entries(provider.availability).map(([day, schedule]) => {
                      const dayName = dayNames[day] || day;
                      return (
                        <div key={day} className={styles.availabilityRow}>
                          <span className={styles.day}>{dayName}</span>
                          <span className={`${styles.hours} ${!schedule?.available ? styles.closed : ''}`}>
                            {schedule?.available 
                              ? `${schedule.start || '09:00'} - ${schedule.end || '17:00'}` 
                              : closed
                            }
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Actions, Gallery & Reviews */}
          <div className={styles.rightColumn}>
            {/* Quick Actions */}
            <div className={styles.actionsCard}>
              <h3>Contact Provider</h3>
              <div className={styles.actionsGrid}>
                <button className={`${styles.actionBtn} ${styles.phone}`} onClick={handleCall}>
                  <Phone size={20} />
                  <span>Call</span>
                </button>
                <button className={`${styles.actionBtn} ${styles.whatsapp}`} onClick={handleWhatsApp}>
                  <MessageCircle size={20} />
                  <span>WhatsApp</span>
                </button>
                <button className={`${styles.actionBtn} ${styles.email}`} onClick={handleEmail}>
                  <Mail size={20} />
                  <span>Email</span>
                </button>
                <button className={`${styles.actionBtn} ${styles.directions}`} onClick={handleDirection}>
                  <Navigation size={20} />
                  <span>{directions}</span>
                </button>
              </div>
            </div>

            {/* Gallery Section */}
            {galleryImages && galleryImages.length > 0 && (
              <div className={styles.galleryCard}>
                <ProviderGallery 
                  images={galleryImages} 
                  onImageClick={handleOpenGallery}
                />
              </div>
            )}

            {/* Reviews Section */}
            <div className={styles.reviewsCard}>
              <div className={styles.reviewsHeader}>
                <h3>
                  <MessageSquare size={20} />
                  {reviewsLabel}
                </h3>
                <button className={styles.writeReviewBtn} onClick={() => setModalVisible(true)}>
                  {writeReview}
                </button>
              </div>

              {reviews && reviews.length > 0 ? (
                <div className={styles.reviewsList}>
                  {reviews.map((review, index) => (
                    <ReviewItem key={review._id || index} review={review} />
                  ))}
                </div>
              ) : (
                <div className={styles.noReviews}>
                  <Heart size={48} className={styles.noReviewsIcon} />
                  <p className={styles.noReviewsTitle}>{noReviewsYet}</p>
                  <p className={styles.noReviewsSubtitle}>{beFirstReview}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Modal */}
      {galleryModalVisible && (
        <GalleryModal
          images={galleryImages}
          onClose={handleCloseGallery}
          initialImage={selectedGalleryImage}
        />
      )}

      {/* Review Modal */}
      {modalVisible && (
        <div className={styles.modalOverlay} onClick={() => setModalVisible(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{writeReviewTitle}</h2>
              <button className={styles.modalClose} onClick={() => setModalVisible(false)}>
                <X size={24} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalProvider}>
                <img 
                  src={provider?.profileImage?.url || 'https://via.placeholder.com/80'} 
                  alt={translatedBusinessName} 
                />
                <div>
                  <div className={styles.modalProviderName}>{translatedBusinessName}</div>
                  <div className={styles.modalProviderService}>{translatedServiceType}</div>
                </div>
              </div>

              <div className={styles.modalRating}>
                <label>{yourRating}</label>
                <div className={styles.ratingText}>
                  {selectedStar > 0 
                    ? `${selectedStar} ${selectedStar > 1 ? starsLabel : starLabel}` 
                    : tapStarToRate
                  }
                </div>
                {renderStars()}
              </div>

              <div className={styles.modalField}>
                <label>{yourName}</label>
                <input
                  type="text"
                  placeholder={namePlaceholder}
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.modalField}>
                <label>{phoneOptional}</label>
                <input
                  type="tel"
                  placeholder={phonePlaceholder}
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.modalField}>
                <label>{yourReview}</label>
                <textarea
                  placeholder={reviewPlaceholder}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  disabled={isSubmitting}
                  maxLength={500}
                />
                <div className={styles.charCount}>{comment.length}/500</div>
              </div>

              <button 
                className={styles.submitReviewBtn}
                onClick={handleSubmitReview}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 size={20} className={styles.spinner} />
                ) : (
                  <>
                    <Send size={20} />
                    {submitReview}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboard}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © 2026 {homeTitle}, {madeWithLove}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProviderDetailPage;